import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingCart, Percent, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmt = (v) => `£${Number(v||0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DebtPurchaseDialog({ open, onClose, debt, api, onSaved, editPurchase = null }) {
  const isEdit = !!editPurchase;
  const [amount, setAmount]           = useState("");
  const [date, setDate]               = useState(todayISO());
  const [description, setDescription] = useState("");
  const [category, setCategory]       = useState("");
  const [interestType, setInterestType] = useState("0_percent");
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editPurchase) {
      setAmount(String(editPurchase.amount ?? ""));
      setDate(editPurchase.date || todayISO());
      setDescription(editPurchase.description || "");
      setCategory(editPurchase.category || "");
      setInterestType(editPurchase.interest_type || "0_percent");
    } else {
      setAmount(""); setDate(todayISO());
      setDescription(""); setCategory("");
      setInterestType("0_percent"); // default per user spec
    }
  }, [open, isEdit, editPurchase]);

  const amt = Number(amount) || 0;
  const oldAmt = isEdit ? Number(editPurchase?.amount || 0) : 0;
  const delta  = amt - oldAmt;
  const projTotal   = (Number(debt?.total_balance)   || 0) + delta;
  const projCurrent = (Number(debt?.current_balance) || 0) + delta;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (amt <= 0) { toast.error("Amount must be greater than zero"); return; }
    setSaving(true);
    try {
      const body = {
        amount: amt, date, description: description.trim(),
        category: category.trim() || "purchase",
        interest_type: interestType,
      };
      if (isEdit) {
        await axios.put(`${api}/debts/${debt.id}/purchase/${editPurchase.id}`, body);
        toast.success(`Updated purchase — ${fmt(amt)}`);
      } else {
        await axios.post(`${api}/debts/${debt.id}/purchase`, body);
        toast.success(`Recorded ${fmt(amt)} purchase against ${debt.name}`);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${isEdit ? "update" : "record"} purchase`);
    } finally { setSaving(false); }
  };

  const intBtn = (val, Icon, label, color) => (
    <button
      type="button"
      data-testid={`interest-type-${val}`}
      onClick={() => setInterestType(val)}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-sm border text-xs font-mono-data uppercase tracking-wider transition-colors ${
        interestType === val
          ? `${color} border-current ring-1 ring-current/30`
          : "border-gray-200 text-gray-500 hover:bg-gray-50"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2} />
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-sm border border-gray-200" data-testid="debt-purchase-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
            <ShoppingCart className="w-5 h-5 text-red-600" strokeWidth={1.75} />
            {isEdit ? "Edit Purchase" : "Record Purchase"} — {debt?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 pt-1">
            {isEdit
              ? "Editing this purchase fully reverses the original amount, then applies the new one. Both TOTAL BALANCE and CURRENT BAL adjust by the net delta."
              : "Adds new spending to this debt's balance. Both TOTAL BALANCE and CURRENT BAL will increase by the entered amount."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Amount (£) *</Label>
            <Input
              type="number" step="0.01" min="0.01" required autoFocus
              data-testid="purchase-amount-input"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="rounded-sm mt-1 font-mono-data" placeholder="e.g. 49.99"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Date</Label>
              <Input
                type="date" data-testid="purchase-date-input"
                value={date} onChange={(e) => setDate(e.target.value)}
                className="rounded-sm mt-1 font-mono-data text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Category</Label>
              <Input
                data-testid="purchase-category-input"
                value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. groceries"
                className="rounded-sm mt-1 text-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
            <Input
              data-testid="purchase-description-input"
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Amazon purchase"
              className="rounded-sm mt-1 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Interest Rate</Label>
            <div className="flex gap-2 mt-1" data-testid="interest-type-group">
              {intBtn("0_percent", Percent, "0% Promo", "text-green-700 bg-green-50")}
              {intBtn("standard",  TrendingUp, "Standard APR", "text-red-700 bg-red-50")}
            </div>
            <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">
              0% promo: purchase sits in the promotional bucket, no interest accruing. Standard APR: purchase accrues at the debt's standard rate.
            </p>
          </div>
          {amt > 0 && (
            <div data-testid="purchase-preview" className="border border-red-200 bg-red-50 rounded-sm px-3 py-2 text-xs">
              <div className="text-gray-600">{isEdit ? "After update:" : "After saving:"}</div>
              <div className="font-mono-data mt-1 text-gray-800">TOTAL BAL: {fmt(debt?.total_balance)} → <strong className="text-red-700">{fmt(projTotal)}</strong></div>
              <div className="font-mono-data text-gray-800">CURRENT BAL: {fmt(debt?.current_balance)} → <strong className="text-red-700">{fmt(projCurrent)}</strong></div>
              {isEdit && delta !== 0 && (
                <div className="font-mono-data text-gray-600 mt-1 text-[10px]">
                  Net delta: {delta > 0 ? "+" : ""}{fmt(delta)}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="purchase-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || amt <= 0} data-testid="purchase-submit" className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Record Purchase"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
